import { useState, useEffect } from "react";
import { format } from "date-fns";
import { Calendar, MessageSquare } from "lucide-react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useCoachContext } from "@/contexts/CoachContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

const bookingSchema = z.object({
  firstName: z.string().trim().min(1, "First name is required").max(50),
  lastName: z.string().trim().min(1, "Last name is required").max(50),
  email: z.string().trim().email("Invalid email address").max(255),
  phone: z.string().trim().min(10, "Phone number must be at least 10 digits").max(20),
});

const requestSchema = z.object({
  firstName: z.string().trim().min(1, "First name is required").max(50),
  lastName: z.string().trim().min(1, "Last name is required").max(50),
  email: z.string().trim().email("Invalid email address").max(255),
  phone: z.string().trim().max(20).optional(),
  message: z.string().trim().max(500).optional(),
  preferredDate: z.string().optional(),
  preferredTime: z.string().optional(),
});

type BookingFormValues = z.infer<typeof bookingSchema>;
type RequestFormValues = z.infer<typeof requestSchema>;

interface AvailabilitySlot {
  id: string;
  date: string;
  start_time: string;
  end_time: string;
  coach_id: string;
}

interface BookingDialogProps {
  trigger?: React.ReactNode;
}

const BookingDialog = ({ trigger }: BookingDialogProps) => {
  const { coachId } = useCoachContext();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<"form" | "calendar" | "request" | "success">("form");
  const [mode, setMode] = useState<"booking" | "request">("booking");
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [selectedSlot, setSelectedSlot] = useState<AvailabilitySlot | null>(null);
  const [availableSlots, setAvailableSlots] = useState<AvailabilitySlot[]>([]);
  const [availableDates, setAvailableDates] = useState<Date[]>([]);
  const [hasAvailability, setHasAvailability] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const form = useForm<BookingFormValues>({
    resolver: zodResolver(bookingSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    },
  });

  const requestForm = useForm<RequestFormValues>({
    resolver: zodResolver(requestSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      message: "",
      preferredDate: "",
      preferredTime: "",
    },
  });

  // Check if coach has any availability when dialog opens
  useEffect(() => {
    if (open) {
      checkAvailability();
    }
  }, [open, coachId]);

  // Fetch slots when date is selected
  useEffect(() => {
    if (selectedDate) {
      fetchSlotsForDate(selectedDate);
    }
  }, [selectedDate]);

  const checkAvailability = async () => {
    setIsLoading(true);
    const query = supabase
      .from("coach_availability")
      .select("date")
      .eq("is_booked", false)
      .gte("date", format(new Date(), "yyyy-MM-dd"))
      .order("date");

    // Only filter by coach if we have a coachId
    if (coachId) {
      query.eq("coach_id", coachId);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error checking availability:", error);
      setHasAvailability(false);
    } else {
      const uniqueDates = [...new Set(data?.map((d) => d.date))];
      setAvailableDates(uniqueDates.map((d) => new Date(d + "T00:00:00")));
      setHasAvailability(uniqueDates.length > 0);
      
      // If no availability, switch to request mode
      if (uniqueDates.length === 0) {
        setMode("request");
      } else {
        setMode("booking");
      }
    }
    setIsLoading(false);
  };

  const fetchSlotsForDate = async (date: Date) => {
    setIsLoading(true);
    const dateStr = format(date, "yyyy-MM-dd");
    
    const query = supabase
      .from("coach_availability")
      .select("*")
      .eq("date", dateStr)
      .eq("is_booked", false)
      .order("start_time");

    if (coachId) {
      query.eq("coach_id", coachId);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching slots:", error);
    } else {
      setAvailableSlots(data || []);
    }
    setIsLoading(false);
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const onFormSubmit = (data: BookingFormValues) => {
    if (mode === "request" || !hasAvailability) {
      // Copy data to request form and show request form
      requestForm.setValue("firstName", data.firstName);
      requestForm.setValue("lastName", data.lastName);
      requestForm.setValue("email", data.email);
      requestForm.setValue("phone", data.phone);
      setStep("request");
    } else {
      setStep("calendar");
    }
  };

  const onRequestSubmit = async (data: RequestFormValues) => {
    if (!coachId) {
      toast({
        title: "Error",
        description: "Unable to submit request. Please try again.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    const { error } = await supabase.from("booking_requests").insert({
      coach_id: coachId,
      first_name: data.firstName,
      last_name: data.lastName,
      email: data.email,
      phone: data.phone || null,
      message: data.message || null,
      preferred_date: data.preferredDate || null,
      preferred_time: data.preferredTime || null,
    });

    if (error) {
      console.error("Request error:", error);
      toast({
        title: "Request Failed",
        description: "Unable to submit your request. Please try again.",
        variant: "destructive",
      });
    } else {
      setStep("success");
    }
    setIsSubmitting(false);
  };

  const handleBookSlot = async () => {
    if (!selectedSlot || !selectedDate) return;

    setIsSubmitting(true);
    const formData = form.getValues();

    // Create booking
    const { data: bookingData, error: bookingError } = await supabase.from("bookings").insert({
      coach_id: selectedSlot.coach_id,
      availability_id: selectedSlot.id,
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      phone: formData.phone,
    }).select().single();

    if (bookingError) {
      console.error("Booking error:", bookingError);
      toast({
        title: "Booking Failed",
        description: "Unable to complete your booking. Please try again.",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    // Mark slot as booked
    const { error: updateError } = await supabase
      .from("coach_availability")
      .update({ is_booked: true })
      .eq("id", selectedSlot.id);

    if (updateError) {
      console.error("Update error:", updateError);
    }

    // Send email notifications
    try {
      await supabase.functions.invoke("send-booking-notification", {
        body: {
          bookingId: bookingData.id,
        },
      });
    } catch (emailError) {
      console.error("Email notification error:", emailError);
    }

    setIsSubmitting(false);
    setStep("success");
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => {
      setStep("form");
      setMode("booking");
      setSelectedDate(undefined);
      setSelectedSlot(null);
      setHasAvailability(null);
      form.reset();
      requestForm.reset();
    }, 300);
  };

  const isDateAvailable = (date: Date) => {
    return availableDates.some(
      (d) => format(d, "yyyy-MM-dd") === format(date, "yyyy-MM-dd")
    );
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => isOpen ? setOpen(true) : handleClose()}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="gold" size="lg" className="group">
            <Calendar className="w-5 h-5" />
            Schedule a Call
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        {step === "form" && (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">
                {isLoading ? "Loading..." : hasAvailability === false ? "Request a Call" : "Book Your Discovery Call"}
              </DialogTitle>
              <DialogDescription>
                {hasAvailability === false 
                  ? "Fill in your details and we'll get back to you to schedule a call."
                  : "Please provide your contact information to proceed with booking."}
              </DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onFormSubmit)} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name</FormLabel>
                        <FormControl>
                          <Input placeholder="John" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Doe" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="john@example.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone Number</FormLabel>
                      <FormControl>
                        <Input type="tel" placeholder="+1 (555) 000-0000" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-full mt-6" disabled={isLoading}>
                  {isLoading ? "Checking availability..." : hasAvailability === false ? "Continue" : "Continue to Calendar"}
                </Button>
              </form>
            </Form>
          </>
        )}

        {step === "request" && (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">Request a Session</DialogTitle>
              <DialogDescription>
                No calendar slots available right now. Submit your request and we'll contact you to schedule.
              </DialogDescription>
            </DialogHeader>
            <Form {...requestForm}>
              <form onSubmit={requestForm.handleSubmit(onRequestSubmit)} className="space-y-4 mt-4">
                <FormField
                  control={requestForm.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Message (optional)</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Tell us about your goals or what you'd like to discuss..."
                          className="min-h-[100px]"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={requestForm.control}
                    name="preferredDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Preferred Date</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={requestForm.control}
                    name="preferredTime"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Preferred Time</FormLabel>
                        <FormControl>
                          <Input type="time" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" onClick={() => setStep("form")} className="flex-1">
                    Back
                  </Button>
                  <Button type="submit" disabled={isSubmitting} className="flex-1">
                    {isSubmitting ? "Submitting..." : "Submit Request"}
                  </Button>
                </div>
              </form>
            </Form>
          </>
        )}

        {step === "calendar" && (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">Select a Time Slot</DialogTitle>
              <DialogDescription>
                Choose an available date and time for your discovery call.
              </DialogDescription>
            </DialogHeader>
            
            <div className="mt-4 space-y-6">
              {isLoading && availableDates.length === 0 ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-pulse text-muted-foreground">Loading availability...</div>
                </div>
              ) : (
                <>
                  <CalendarComponent
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={(date) => !isDateAvailable(date) || date < new Date()}
                    className="rounded-md border pointer-events-auto"
                  />

                  {selectedDate && (
                    <div className="space-y-3">
                      <h4 className="font-medium text-sm">
                        Available times for {format(selectedDate, "MMMM d, yyyy")}:
                      </h4>
                      {isLoading ? (
                        <div className="animate-pulse text-muted-foreground text-sm">Loading slots...</div>
                      ) : availableSlots.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No available slots for this date.</p>
                      ) : (
                        <div className="grid grid-cols-2 gap-2">
                          {availableSlots.map((slot) => (
                            <button
                              key={slot.id}
                              onClick={() => setSelectedSlot(slot)}
                              className={cn(
                                "p-3 rounded-lg border text-sm transition-colors",
                                selectedSlot?.id === slot.id
                                  ? "border-primary bg-primary/10 text-primary"
                                  : "border-border hover:border-primary/50"
                              )}
                            >
                              {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => setStep("form")} className="flex-1">
                      Back
                    </Button>
                    <Button
                      onClick={handleBookSlot}
                      disabled={!selectedSlot || isSubmitting}
                      className="flex-1"
                    >
                      {isSubmitting ? "Booking..." : "Confirm Booking"}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {step === "success" && (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-center">
                {mode === "request" ? "Request Submitted!" : "Booking Confirmed!"}
              </DialogTitle>
            </DialogHeader>
            <div className="text-center py-6 space-y-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                {mode === "request" ? (
                  <MessageSquare className="w-8 h-8 text-primary" />
                ) : (
                  <Calendar className="w-8 h-8 text-primary" />
                )}
              </div>
              {mode === "request" ? (
                <>
                  <p className="text-muted-foreground">
                    Thank you for your interest! We've received your request and will contact you soon to schedule a call.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Please check your email for confirmation.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-muted-foreground">
                    Your discovery call has been scheduled for{" "}
                    <span className="font-medium text-foreground">
                      {selectedDate && format(selectedDate, "MMMM d, yyyy")}
                    </span>
                    {selectedSlot && (
                      <>
                        {" "}at{" "}
                        <span className="font-medium text-foreground">
                          {formatTime(selectedSlot.start_time)}
                        </span>
                      </>
                    )}
                    .
                  </p>
                  <p className="text-sm text-muted-foreground">
                    You will receive a confirmation email shortly.
                  </p>
                </>
              )}
              <Button onClick={handleClose} className="mt-4">
                Done
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookingDialog;
